import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Refresh,
  Download,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { btApi, BTSession, BTAttendanceRecord, BTClub, BTLocation } from '../../services/api/btApi';

const BTAttendancePage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [sessionInfo, setSessionInfo] = useState<BTSession | null>(null);
  const [attendanceRecords, setAttendanceRecords] = useState<BTAttendanceRecord[]>([]);
  const [clubs, setClubs] = useState<BTClub[]>([]);
  const [locations, setLocations] = useState<BTLocation[]>([]);
  const [manualDialog, setManualDialog] = useState(false);
  const [manualFormData, setManualFormData] = useState({
    teacherName: '',
    teacherPhone: '',
    keyCode: '',
    status: 'present' as 'present' | 'late' | 'absent',
    notes: '',
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    if (sessionId) {
      fetchData();
    }
  }, [sessionId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 병렬로 데이터 불러오기
      const [clubsData, locationsData] = await Promise.all([
        btApi.getClubs(),
        btApi.getLocations(),
      ]);

      setClubs(clubsData || []);
      setLocations(locationsData || []);

      // 세션 정보 불러오기
      if (sessionId) {
        try {
          const sessionData = await btApi.getSessionBySessionId(sessionId);
          setSessionInfo(sessionData);
        } catch (sessionError) {
          console.log('세션 정보를 찾을 수 없습니다:', sessionError);
          // 세션 정보가 없어도 출석 목록은 조회 가능
        }
      }

      // 출석 목록 조회 (세션 ID로 필터링)
      const params: Record<string, string> = {};
      if (sessionId) {
        params.session_id = sessionId;
      }
      const attendanceData = await btApi.getAttendanceList(params);
      setAttendanceRecords(attendanceData.data || []);

    } catch (error: any) {
      console.error('데이터 조회 오류:', error);
      setSnackbar({
        open: true,
        message: '데이터를 불러오는데 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManualCheckIn = async () => {
    try {
      // 수동 출석 등록 - 실제 API 호출
      const response = await fetch('/api/bt/bt_attendance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: manualFormData.teacherName,
          teacher_phone: manualFormData.teacherPhone,
          teacher_key: manualFormData.keyCode,
          session_id: sessionId,
        }),
      });

      if (!response.ok) {
        throw new Error('출석 등록에 실패했습니다.');
      }

      setSnackbar({
        open: true,
        message: `${manualFormData.teacherName}님이 수동으로 출석 처리되었습니다.`,
        severity: 'success',
      });

      // 데이터 새로고침
      fetchData();

      setManualDialog(false);
      setManualFormData({
        teacherName: '',
        teacherPhone: '',
        keyCode: '',
        status: 'present',
        notes: '',
      });

    } catch (error: any) {
      console.error('수동 출석 오류:', error);
      setSnackbar({
        open: true,
        message: error.message || '수동 출석 처리 중 오류가 발생했습니다.',
        severity: 'error',
      });
    }
  };

  const getEducationTypeText = (type?: string) => {
    switch (type) {
      case 'new': return '신규교육';
      case 'renewal': return '재교육';
      default: return '-';
    }
  };

  const getClubName = (clubId?: string) => {
    if (!clubId) return '-';
    const club = clubs.find(c => c._id === clubId);
    return club?.bt_club_name || clubId;
  };

  const getLocationName = (locationId?: string) => {
    if (!locationId) return '-';
    const location = locations.find(l => l._id === locationId);
    return location?.location_name || locationId;
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleExcelDownload = () => {
    const exportData = attendanceRecords.map(record => ({
      '교사명': record.name || '-',
      '연락처': record.teacher_phone || '-',
      '교회명': record.church_name || '-',
      '교회번호': record.church_id || '-',
      '클럽': getClubName(record.bt_club_name),
      '교육형태': getEducationTypeText(record.education_type),
      '수강지역': getLocationName(record.bt_location_name),
      '키 코드': record.teacher_key || '-',
      '등록일': formatDateTime(record.createdAt),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'BT출석기록');

    const fileName = sessionInfo
      ? `BT출석_${sessionInfo.title}_${new Date().toISOString().split('T')[0]}.xlsx`
      : `BT출석_${new Date().toISOString().split('T')[0]}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };

  // 통계 계산 - 이 세션에 등록된 출석 수
  const totalCount = attendanceRecords.length;
  const newEducationCount = attendanceRecords.filter(r => r.education_type === 'new').length;
  const renewalEducationCount = attendanceRecords.filter(r => r.education_type === 'renewal').length;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/bt')}
          variant="outlined"
        >
          BT 관리로
        </Button>
        <Typography variant="h4" component="h1">
          출결 관리 - {sessionInfo?.title}
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={fetchData}
          variant="outlined"
        >
          새로고침
        </Button>
      </Box>

      {/* 세션 정보 카드: Basic Training과 리더십 학교 모두 표시 */}
      {sessionInfo ? (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" gutterBottom>세션 정보</Typography>
                <Typography variant="body2">제목: {sessionInfo.title}</Typography>
                <Typography variant="body2">교육 구분: {sessionInfo.trainingCategory === 'leadership-education' ? '리더십 학교' : 'Basic Training'}</Typography>
                <Typography variant="body2">설명: {sessionInfo.description || '-'}</Typography>
                <Typography variant="body2">장소: {sessionInfo.location || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" gutterBottom>일정</Typography>
                <Typography variant="body2">
                  날짜: {sessionInfo.date ? new Date(sessionInfo.date).toLocaleDateString('ko-KR') : '-'}
                </Typography>
                <Typography variant="body2">
                  시간: {sessionInfo.startTime || '-'} - {sessionInfo.endTime || '-'}
                </Typography>
                <Typography variant="body2">
                  상태: <Chip label={sessionInfo.status || '알 수 없음'} color="primary" size="small" />
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : sessionId && (
        <Alert severity="info" sx={{ mb: 4 }}>
          세션 ID: {sessionId} - 세션 정보를 찾을 수 없습니다. 아래에서 해당 세션의 출석 기록을 확인할 수 있습니다.
        </Alert>
      )}

      {/* 출결 통계 카드 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>총 출석</Typography>
              <Typography variant="h4">{totalCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>신규교육</Typography>
              <Typography variant="h4" color="primary.main">{newEducationCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>재교육</Typography>
              <Typography variant="h4" color="secondary.main">{renewalEducationCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>세션 ID</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {sessionId || '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 액션 버튼 */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<Download />}
          onClick={handleExcelDownload}
          disabled={attendanceRecords.length === 0}
        >
          엑셀 다운로드
        </Button>
        <Button
          variant="outlined"
          startIcon={<Person />}
          onClick={() => setManualDialog(true)}
        >
          수동 출석
        </Button>
      </Box>

      {/* 출결 기록 테이블 */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>교사명</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>연락처</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>교회명</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>클럽</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>교육형태</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>수강지역</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>키 코드</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>등록일시</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    데이터를 불러오는 중...
                  </TableCell>
                </TableRow>
              ) : attendanceRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    등록된 출석 기록이 없습니다.
                  </TableCell>
                </TableRow>
              ) : attendanceRecords.map((record) => (
                <TableRow key={record._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {record.name || (typeof record.teacherId === 'object' ? record.teacherId.teacherName : '') || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {record.teacher_phone || (typeof record.teacherId === 'object' ? record.teacherId.teacherPhone : '') || '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {record.church_name || (typeof record.teacherId === 'object' ? record.teacherId.churchName : '') || '-'}
                    </Typography>
                    {record.church_id && (
                      <Typography variant="caption" color="text.secondary">
                        ({record.church_id})
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{record.bt_club_name || record.sessionType || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={record.sessionId || record.session_id
                        ? '리더십 학교'
                        : getEducationTypeText(record.education_type)}
                      color={record.sessionId || record.session_id || sessionInfo?.trainingCategory === 'leadership-education'
                        ? 'secondary'
                        : record.education_type === 'new' ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{record.bt_location_name || record.location || '-'}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                      }}
                    >
                      {record.teacher_key || record.keyCode || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDateTime(record.checkInTime || record.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 수동 출석 다이얼로그 */}
      <Dialog open={manualDialog} onClose={() => setManualDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>수동 출석 처리</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="교사 이름"
              value={manualFormData.teacherName}
              onChange={(e) => setManualFormData({...manualFormData, teacherName: e.target.value})}
              sx={{ mb: 2 }}
              required
            />
            
            <TextField
              fullWidth
              label="전화번호"
              value={manualFormData.teacherPhone}
              onChange={(e) => setManualFormData({...manualFormData, teacherPhone: e.target.value})}
              sx={{ mb: 2 }}
              required
            />
            
            <TextField
              fullWidth
              label="키 코드"
              value={manualFormData.keyCode}
              onChange={(e) => setManualFormData({...manualFormData, keyCode: e.target.value})}
              sx={{ mb: 2 }}
              required
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>출결 상태</InputLabel>
              <Select
                value={manualFormData.status}
                onChange={(e) => setManualFormData({...manualFormData, status: e.target.value as any})}
              >
                <MenuItem value="present">출석</MenuItem>
                <MenuItem value="late">지각</MenuItem>
                <MenuItem value="absent">결석</MenuItem>
                <MenuItem value="early-leave">조기퇴실</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="비고"
              value={manualFormData.notes}
              onChange={(e) => setManualFormData({...manualFormData, notes: e.target.value})}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDialog(false)}>취소</Button>
          <Button
            variant="contained"
            onClick={handleManualCheckIn}
            disabled={!manualFormData.teacherName || !manualFormData.teacherPhone || !manualFormData.keyCode}
          >
            출석 처리
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({...snackbar, open: false})}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BTAttendancePage;
